import { useCallback, useEffect, useRef } from 'react';
import { type IntegrationConnectType } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

export interface UseIntegrationConnectFunnel {
  trackConnectStarted: (properties?: Record<string, unknown>) => void;
  trackConnectFailed: (reason: string, properties?: Record<string, unknown>) => void;
}

// Unified connect-funnel analytics for one integration sheet: emits started/failed
// explicitly and fires succeeded automatically (once) when `isConnected` flips
// false -> true after a started attempt in the same session.
export const useIntegrationConnectFunnel = (
  integrationType: IntegrationConnectType,
  isConnected: boolean,
): UseIntegrationConnectFunnel => {
  const { capture } = usePostHogAnalytics();
  const attemptInFlightRef = useRef(false);
  const prevConnectedRef = useRef(isConnected);

  const trackConnectStarted = useCallback(
    (properties?: Record<string, unknown>) => {
      attemptInFlightRef.current = true;
      trackIntegrationConnectStarted(capture, integrationType, properties);
    },
    [capture, integrationType],
  );

  const trackConnectFailed = useCallback(
    (reason: string, properties?: Record<string, unknown>) => {
      // Only a started attempt can fail, so a shared error handler reused by a
      // reconnect (no started) stays out of the funnel.
      if (!attemptInFlightRef.current) return;
      attemptInFlightRef.current = false;
      trackIntegrationConnectFailed(capture, integrationType, reason, properties);
    },
    [capture, integrationType],
  );

  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = isConnected;
    // Succeeded only counts a fresh connection that follows a started attempt.
    if (!wasConnected && isConnected && attemptInFlightRef.current) {
      attemptInFlightRef.current = false;
      trackIntegrationConnectSucceeded(capture, integrationType);
    }
  }, [isConnected, capture, integrationType]);

  return { trackConnectStarted, trackConnectFailed };
};
